import { useEffect, useRef } from 'react';

import {
  type LinkDetailTelemetryViewModel,
  type LinkTelemetryBucketViewModel,
  type LinkTelemetryEndpointViewModel,
  type LinkTelemetryMetricViewModel,
} from '../linkDetailTelemetry';
import { type Link } from '../NetworkMap';
import { LinkTelemetryChart } from './LinkTelemetryChart';
import { formatAge, formatValue } from './helpers';

// These are defined in the prompt.
const TRAFFIC_SERIES_RANGE_OPTIONS = [
  { label: 'Real time', seconds: 5 * 60, realtime: true },
  { label: '5m', seconds: 5 * 60 },
  { label: '15m', seconds: 15 * 60 },
  { label: '30m', seconds: 30 * 60 },
  { label: '1h', seconds: 60 * 60 },
  { label: '3h', seconds: 3 * 60 * 60 },
  { label: '6h', seconds: 6 * 60 * 60 },
  { label: '12h', seconds: 12 * 60 * 60 },
  { label: '1d', seconds: 24 * 60 * 60 },
  { label: '7d', seconds: 7 * 24 * 60 * 60 },
  { label: '30d', seconds: 30 * 24 * 60 * 60 },
  { label: '90d', seconds: 90 * 24 * 60 * 60 },
  { label: '1y', seconds: 365 * 24 * 60 * 60 },
] as const;

const telemetryCategoryLabel = (category: string) =>
  category.charAt(0).toUpperCase() + category.slice(1);

const telemetryStateLabel = (state: string) =>
  ({
    available: 'Available',
    stale: 'Stale',
    partial: 'Partial data',
    failed: 'Collection failed',
    unsupported: 'Unsupported',
    unavailable: 'Unavailable',
    no_samples_in_range: 'No samples in range',
    disabled: 'Collection disabled',
    loading: 'Loading',
  })[state] ?? state.replace(/_/g, ' ');

const formatMbps = (value?: number) =>
  Number.isFinite(value) ? `${(Number(value) / 1_000_000).toFixed(2)} Mbps` : '—';

const formatOperStatus = (value?: number) => {
  if (!Number.isFinite(value)) return '—';
  const label = ({ 1: 'up', 2: 'down', 3: 'testing', 4: 'unknown', 5: 'dormant', 6: 'not present', 7: 'lower-layer down' })[
    Number(value)
  ];
  return label ? `${label} (${value})` : String(value);
};

const metricContext = (metric: LinkTelemetryMetricViewModel) => {
  const context: string[] = [];
  if (metric.role) context.push(`Role: ${metric.role.replace(/_/g, ' ')}`);
  if (metric.health) context.push(`Health: ${metric.health}`);
  context.push(`State: ${telemetryStateLabel(metric.state)}`);
  if (metric.observedAt) context.push(`Observed ${formatAge(Date.parse(metric.observedAt))}`);
  const thresholds = [
    metric.warningMin != null ? `warn low ${formatValue(metric.warningMin, metric.unit)}` : '',
    metric.warningMax != null ? `warn high ${formatValue(metric.warningMax, metric.unit)}` : '',
    metric.criticalMin != null ? `critical low ${formatValue(metric.criticalMin, metric.unit)}` : '',
    metric.criticalMax != null ? `critical high ${formatValue(metric.criticalMax, metric.unit)}` : '',
  ].filter(Boolean);
  if (thresholds.length) context.push(`Thresholds: ${thresholds.join(', ')}`);
  return context.join(' · ');
};

export type LinkDetailDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  viewModel: LinkDetailTelemetryViewModel;
  rawLink: Link | null;
  capacity: string;
  utilization: string;
  // Will be controlled by a new controller hook
  sheet: 'graphs' | 'details';
  onSheetChange: (sheet: 'graphs' | 'details') => void;
  rangeSeconds: number;
  onRangeSecondsChange: (seconds: number) => void;
  isRealtime: boolean;
  onIsRealtimeChange: (isRealtime: boolean) => void;
  onOpenGrafana: (target: { deviceId: string; ifindex?: number }) => void;
  isGrafanaAvailable: boolean;
  onRetry: () => void;
};

const getStatusPresentation = (status: Link['status']) => {
  switch (status) {
    case 'healthy':
      return {
        label: 'Healthy',
        dotClass: 'bg-[color:var(--color-success)]',
        textClass: 'text-[color:var(--color-success)]',
        borderClass: 'border-border/35 bg-card/25',
      };
    case 'warning':
      return {
        label: 'Warning',
        dotClass: 'bg-[color:var(--color-warning)]',
        textClass: 'text-[color:var(--color-warning)]',
        borderClass: 'border-border/35 bg-card/25',
      };
    case 'broken':
      return {
        label: 'Down',
        dotClass: 'animate-pulse bg-destructive',
        textClass: 'text-destructive',
        borderClass: 'border-border/35 bg-card/25',
      };
    default:
      return {
        label: 'Missing Data',
        dotClass: 'bg-[color:var(--color-info)]',
        textClass: 'text-[color:var(--color-info)]',
        borderClass: 'border-border/35 bg-card/25',
      };
  }
};

const DetailSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-lg border border-border/35 bg-card/25 px-4 py-3">
    <div className="mb-2.5 text-[9px] font-bold uppercase tracking-[0.22em] text-muted-foreground/60">{title}</div>
    <div className="space-y-2.5">{children}</div>
  </div>
);

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 text-[11px]">
    <span className="shrink-0 text-muted-foreground/70">{label}</span>
    <span className="break-words text-right font-medium text-foreground">{value}</span>
  </div>
);

const formatLagMemberEndpoint = (
  deviceName?: string | null,
  interfaceName?: string | null,
  deviceId?: string | null,
  interfaceId?: string | null,
) => {
  const cleanDevice = (deviceName ?? '').trim();
  const cleanInterface = (interfaceName ?? '').trim();
  const fallbackDevice = (deviceId ?? '').trim();
  const fallbackInterface = (interfaceId ?? '').trim();
  const device = cleanDevice || (fallbackDevice ? fallbackDevice.slice(0, 8) + '…' : '');
  const iface = cleanInterface || (fallbackInterface ? fallbackInterface.slice(0, 8) + '…' : '');
  if (device && iface) return `${device} · ${iface}`;
  return device || iface || '—';
};

const EndpointCard = ({
  label,
  host,
  ifName,
  accentColor,
}: {
  label: string;
  host: string;
  ifName: string;
  accentColor: string;
}) => (
  <div
    className="overflow-hidden rounded-lg border border-border/35 bg-card/25"
    style={{ borderLeft: `2px solid ${accentColor}` }}
  >
    <div className="flex items-center justify-between border-b border-border/25 px-3 py-2">
      <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: accentColor }}>
        {label}
      </span>
    </div>
    <div className="space-y-2 px-3 pb-3 pt-2.5">
      <div>
        <div className="text-[8px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/55">Host</div>
        <div className="mt-0.5 truncate text-[11px] font-semibold text-foreground">{host || '—'}</div>
      </div>
      <div>
        <div className="text-[8px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/55">
          Interface
        </div>
        <div className="mt-0.5 truncate text-[11px] font-semibold text-foreground">{ifName || '—'}</div>
      </div>
    </div>
  </div>
);

const OpticalLaneSummary = ({
  title,
  metrics,
  endpoint,
}: {
  title: string;
  metrics: LinkTelemetryMetricViewModel[];
  endpoint: 'A' | 'Z';
}) => {
  const lanes = metrics.filter(m => m.direction === 'rx' || m.direction === 'tx');
  if (lanes.length === 0) return null;

  return (
    <DetailSection title={`${endpoint} End: ${title}`}>
      <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2">
        {lanes.map(m => (
          <DetailRow
            key={m.metricId}
            label={m.lane ? `${m.label} (Lane ${m.lane})` : m.label}
            value={
              <span title={metricContext(m)}>
                {m.currentValue !== undefined ? formatValue(m.currentValue, m.unit) : '—'}
                {m.observedAt && (
                  <span className="ml-1.5 text-[9px] text-muted-foreground/50">
                    ({formatAge(Date.parse(m.observedAt))})
                  </span>
                )}
                <span className="mt-0.5 block text-[9px] font-normal text-muted-foreground/65">
                  {metricContext(m)}
                </span>
              </span>
            }
          />
        ))}
      </div>
    </DetailSection>
  );
};

const ThermalSummary = ({
  bucket,
  endpoint,
}: {
  bucket: LinkTelemetryBucketViewModel | undefined;
  endpoint: 'a' | 'z';
}) => {
  const thermalMetrics =
    bucket?.endpoints[endpoint].metrics.filter(
      m => m.role === 'asic' || m.role === 'npu' || m.role === 'forwarding_chip',
    ) ?? [];

  if (thermalMetrics.length === 0) {
    return (
      <div className="text-center text-[10px] text-muted-foreground/60">ASIC temperature not reported</div>
    );
  }

  return (
    <div className="space-y-2">
      {thermalMetrics.map(m => (
        <div key={m.metricId} className="flex items-center justify-between text-[11px]">
          <span className="text-muted-foreground/80">{m.label}</span>
          <span className="font-semibold text-foreground">
            {m.currentValue !== undefined ? formatValue(m.currentValue, m.unit) : '—'}
            <span className="mt-0.5 block text-right text-[9px] font-normal text-muted-foreground/65">
              {metricContext(m)}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

const EndpointMetricsSummary = ({
  endpoint,
  title,
  accentColor,
  filter,
  emptyState,
}: {
  endpoint: LinkTelemetryEndpointViewModel;
  title: string;
  accentColor: string;
  filter: (m: LinkTelemetryMetricViewModel) => boolean;
  emptyState: React.ReactNode;
}) => {
  const metrics = endpoint.metrics.filter(filter);
  return (
    <div>
      <div
        className="mb-2 text-center text-[9px] font-bold uppercase tracking-[0.2em]"
        style={{ color: accentColor }}
      >
        {title}
      </div>
      {metrics.length === 0 ? (
        <div className="text-center text-[10px] text-muted-foreground/60">{emptyState}</div>
      ) : (
        <div className="space-y-2">
          {metrics.map(m => (
            <div key={m.metricId} className="flex items-baseline justify-between gap-2 text-[11px]">
              <span className="text-muted-foreground/80">
                {m.lane ? `${m.label} (Lane ${m.lane})` : m.label}
              </span>
              <div className="text-right">
                <span className="font-semibold text-foreground">
                  {m.currentValue !== undefined ? formatValue(m.currentValue, m.unit) : '—'}
                </span>
                {m.observedAt && (
                  <span className="ml-1.5 text-[9px] text-muted-foreground/50">
                    ({formatAge(Date.parse(m.observedAt))})
                  </span>
                )}
                <div className="mt-0.5 max-w-56 text-[9px] leading-snug text-muted-foreground/65">
                  {metricContext(m)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const LinkDetailDrawer = ({
  isOpen,
  onClose,
  viewModel,
  rawLink,
  capacity,
  utilization,
  sheet,
  onSheetChange,
  rangeSeconds,
  onRangeSecondsChange,
  isRealtime,
  onIsRealtimeChange,
  onOpenGrafana,
  isGrafanaAvailable,
  onRetry,
}: LinkDetailDrawerProps) => {
  const mobileDialogRef = useRef<HTMLDivElement>(null);
  const desktopDialogRef = useRef<HTMLDivElement>(null);
  const status = getStatusPresentation(rawLink?.status);

  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => {
      const dialog = window.matchMedia('(min-width: 768px)').matches
        ? desktopDialogRef.current
        : mobileDialogRef.current;
      dialog?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  const containMobileFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter(element => element.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      event.currentTarget.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const grafanaEndpoint = (endpoint: 'a' | 'z') => {
    const deviceId = endpoint === 'a' ? rawLink?.source_device_id : rawLink?.target_device_id;
    const ifindex = endpoint === 'a' ? rawLink?.telemetry_ifindex_a : rawLink?.telemetry_ifindex_b;
    if (!deviceId) return null;
    return () => onOpenGrafana({ deviceId, ifindex: ifindex ?? undefined });
  };
  const grafanaA = grafanaEndpoint('a');
  const grafanaZ = grafanaEndpoint('z');

  const opticsBucket = viewModel.buckets.find(b => b.category === 'optics');
  const tempBucket = viewModel.buckets.find(b => b.category === 'temperature');
  const hasDeclaredMissingTelemetry = Boolean(
    rawLink?.missing_telemetry?.source?.length || rawLink?.missing_telemetry?.target?.length,
  );
  const visibleBuckets = viewModel.buckets.filter(
    bucket =>
      ['bandwidth', 'utilization', 'errors', 'packets', 'temperature', 'optics'].includes(
        bucket.category
      ) ||
      bucket.unitGroups.length > 0 ||
      bucket.endpoints.a.metrics.length > 0 ||
      bucket.endpoints.z.metrics.length > 0 ||
      ['failed', 'disabled', 'stale', 'partial'].includes(bucket.state),
  );

  const drawerContent = (
    <div className="flex h-full flex-col bg-background/98 md:bg-[color:var(--bg-primary)]">
      {/* Header */}
      <div className="shrink-0 border-b border-border/35 px-5 pb-4 pt-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {rawLink ? (
              <div className={`mt-[5px] h-2 w-2 shrink-0 rounded-full ${status.dotClass}`} />
            ) : (
              <div className="mt-[5px] h-2 w-2 shrink-0 rounded-full bg-muted" />
            )}
            <div className="min-w-0">
              <div className="text-[9px] font-semibold uppercase tracking-[0.25em] text-muted-foreground/60">
                Network Link
              </div>
              <div className="mt-0.5 truncate text-[15px] font-bold leading-snug tracking-tight text-foreground">
                {rawLink ? rawLink.id : 'Select a link'}
              </div>
              {rawLink ? (
                <div className="mt-1.5">
                  <span
                    className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] ${status.borderClass} ${status.textClass}`}
                  >
                    {status.label}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border/40 bg-muted/15 text-muted-foreground transition hover:bg-muted/35 hover:text-foreground"
            aria-label="Close"
            title="Close"
          >
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" className="h-3 w-3">
              <path d="M1 1l10 10M11 1L1 11" />
            </svg>
          </button>
        </div>
        {rawLink && (
          <div className="mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                  Capacity
                </div>
                <div className="mt-1 text-sm font-bold text-foreground">{capacity}</div>
              </div>
              <div className="text-center">
                <div className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                  Utilization
                </div>
                <div className="mt-1 text-sm font-bold text-foreground">{utilization}</div>
              </div>
            </div>
            {isGrafanaAvailable && (grafanaA || grafanaZ) && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={grafanaA ?? undefined}
                  disabled={!grafanaA}
                  className="rounded border border-border/30 bg-muted/20 px-3 py-1.5 text-center text-[10px] font-semibold text-muted-foreground transition hover:bg-muted/35 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Grafana A-End
                </button>
                <button
                  type="button"
                  onClick={grafanaZ ?? undefined}
                  disabled={!grafanaZ}
                  className="rounded border border-border/30 bg-muted/20 px-3 py-1.5 text-center text-[10px] font-semibold text-muted-foreground transition hover:bg-muted/35 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Grafana Z-End
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="shrink-0 border-b border-border/35 px-5">
        <div className="flex">
          {(['graphs', 'details'] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => onSheetChange(s)}
              className={
                'mr-5 border-b-2 pb-2.5 pt-3 text-[12px] font-semibold transition-colors last:mr-0 ' +
                (sheet === s
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground/75')
              }
            >
              {s === 'graphs' ? 'Graphs' : 'Details'}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto thin-scrollbar">
        {rawLink ? (
          sheet === 'graphs' ? (
            <div className="space-y-5 px-4 pb-4">
              {/* Time range selector */}
              <div className="sticky top-0 z-10 -mx-4 flex flex-wrap items-center gap-2 border-b border-border/35 bg-background/95 px-4 py-2 backdrop-blur">
                <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.22em] text-muted-foreground/60">
                  Range
                </span>
                <div className="flex flex-wrap gap-1">
                  {TRAFFIC_SERIES_RANGE_OPTIONS.map(option => {
                    const optRealtime = ('realtime' in option && Boolean(option.realtime)) as boolean;
                    const isActive = option.seconds === rangeSeconds && optRealtime === isRealtime;
                    return (
                      <button
                        key={`${option.label}-${optRealtime ? 'rt' : 'std'}`}
                        type="button"
                        onClick={() => {
                          onRangeSecondsChange(option.seconds);
                          onIsRealtimeChange(optRealtime);
                        }}
                        className={
                          'min-h-11 rounded px-3 py-2 text-[10px] font-semibold transition-colors ' +
                          (isActive
                            ? 'border border-primary/40 bg-primary/18 text-primary'
                            : 'border border-border/25 text-muted-foreground hover:border-border/50 hover:text-foreground/80')
                        }
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Refresh status */}
              <div className="flex items-center gap-2 text-[9px] text-muted-foreground/60">
                {viewModel.state === 'loading' && (
                  <>
                    <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.2" />
                      <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Loading…</span>
                  </>
                )}
                {viewModel.telemetryUpdatedAt && (
                  <span>Updated {formatAge(Date.parse(viewModel.telemetryUpdatedAt))}</span>
                )}
              </div>

              {(viewModel.state !== 'available' && viewModel.state !== 'loading') ||
              hasDeclaredMissingTelemetry ? (
                <div
                  className="rounded-md border border-border/50 bg-card/35 px-4 py-3"
                  role={viewModel.state === 'failed' ? 'alert' : 'status'}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold text-foreground">
                        Telemetry:{' '}
                        {hasDeclaredMissingTelemetry && viewModel.state === 'available'
                          ? 'Missing endpoint data'
                          : telemetryStateLabel(viewModel.state)}
                      </div>
                      <div className="mt-1 text-[10px] leading-snug text-muted-foreground">
                        {viewModel.diagnostic ||
                          (hasDeclaredMissingTelemetry
                            ? 'One or both endpoints are missing expected telemetry.'
                            : 'Some telemetry may not be available for this link and time range.')}
                      </div>
                    </div>
                    {(viewModel.state === 'failed' ||
                      viewModel.state === 'stale' ||
                      viewModel.state === 'partial') && (
                      <button
                        type="button"
                        onClick={onRetry}
                        className="min-h-11 shrink-0 rounded border border-border/50 px-3 py-2 text-[10px] font-semibold text-foreground transition hover:bg-muted/30"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Bucket charts */}
              <div className="space-y-6">
                {visibleBuckets.map(bucket => (
                    <div
                      key={bucket.category}
                      data-testid={`telemetry-bucket-${bucket.category}`}
                    >
                      <div className="mb-2 flex items-center gap-2.5">
                        <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-muted-foreground/80">
                          {telemetryCategoryLabel(bucket.category)}
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      {bucket.category === 'optics' && opticsBucket && (
                        <div className="mb-3 grid grid-cols-1 gap-4 rounded-lg border border-border/35 bg-card/25 p-3 sm:grid-cols-2">
                          <EndpointMetricsSummary
                            endpoint={opticsBucket.endpoints.a}
                            title="A End"
                            accentColor="var(--color-info)"
                            filter={m => m.direction === 'rx' || m.direction === 'tx'}
                            emptyState="No optical metrics"
                          />
                          <EndpointMetricsSummary
                            endpoint={opticsBucket.endpoints.z}
                            title="Z End"
                            accentColor="var(--color-warning)"
                            filter={m => m.direction === 'rx' || m.direction === 'tx'}
                            emptyState="No optical metrics"
                          />
                        </div>
                      )}
                      {bucket.category === 'temperature' && tempBucket && (
                        <div className="mb-3 grid grid-cols-1 gap-4 rounded-lg border border-border/35 bg-card/25 p-3 sm:grid-cols-2">
                          <EndpointMetricsSummary
                            endpoint={tempBucket.endpoints.a}
                            title="A End"
                            accentColor="var(--color-info)"
                            filter={m =>
                              m.role === 'asic' || m.role === 'npu' || m.role === 'forwarding_chip'
                            }
                            emptyState="ASIC temperature not reported"
                          />
                          <EndpointMetricsSummary
                            endpoint={tempBucket.endpoints.z}
                            title="Z End"
                            accentColor="var(--color-warning)"
                            filter={m =>
                              m.role === 'asic' || m.role === 'npu' || m.role === 'forwarding_chip'
                            }
                            emptyState="ASIC temperature not reported"
                          />
                        </div>
                      )}

                      {bucket.unitGroups.length > 0 ? (
                        bucket.unitGroups.map((unitGroup, i) => (
                          <LinkTelemetryChart
                            key={`${bucket.category}-${unitGroup.unit}-${i}`}
                            unitGroup={unitGroup}
                            bucket={bucket}
                          />
                        ))
                      ) : (
                        <div className="rounded-md border border-dashed border-border/40 bg-card/20 px-4 py-3 text-center">
                          <div className="text-[11px] font-semibold text-foreground">No chart data available</div>
                          <div className="mt-1 text-[10px] text-muted-foreground/80">
                            Status: <span className="font-bold">{telemetryStateLabel(bucket.state)}</span>
                            {bucket.endpoints.a.diagnostic && ` · A: ${bucket.endpoints.a.diagnostic}`}
                            {bucket.endpoints.z.diagnostic && ` · Z: ${bucket.endpoints.z.diagnostic}`}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 px-4 py-4">
              <DetailSection title="Endpoints">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <EndpointCard
                    label="A End"
                    host={rawLink.telemetry_host_a ?? ''}
                    ifName={rawLink.telemetry_ifname_a ?? `ifIndex ${rawLink.telemetry_ifindex_a ?? '?'}`}
                    accentColor="var(--color-info)"
                  />
                  <EndpointCard
                    label="Z End"
                    host={rawLink.telemetry_host_b ?? ''}
                    ifName={rawLink.telemetry_ifname_b ?? `ifIndex ${rawLink.telemetry_ifindex_b ?? '?'}`}
                    accentColor="var(--color-warning)"
                  />
                </div>
              </DetailSection>

              <DetailSection title="Link Info">
                <DetailRow label="Status" value={status.label} />
                {rawLink.status_reason?.summary && (
                  <DetailRow label="Status Reason" value={rawLink.status_reason.summary} />
                )}
                {rawLink.status_reason?.details?.map((detail, i) => (
                  <div
                    key={i}
                    className="rounded border border-border/25 bg-muted/10 px-3 py-2 text-[11px] text-muted-foreground"
                  >
                    {detail}
                  </div>
                ))}
                <DetailRow label="Link Type" value={rawLink.link_type ?? '—'} />
                <DetailRow label="Capacity" value={capacity} />
                <DetailRow label="Utilization" value={utilization} />
                <DetailRow
                  label="COs"
                  value={`${rawLink.source_co_id != null ? `CO ${rawLink.source_co_id}` : '—'} → ${
                    rawLink.target_co_id != null ? `CO ${rawLink.target_co_id}` : '—'
                  }`}
                />
              </DetailSection>

              {rawLink.status_reason ? (
                <DetailSection title="Operational Evidence">
                  {rawLink.status_reason.code && (
                    <DetailRow label="Code" value={rawLink.status_reason.code} />
                  )}
                  {rawLink.status_reason.source_oper_status !== undefined && (
                    <DetailRow
                      label="A end ifOperStatus"
                      value={formatOperStatus(rawLink.status_reason.source_oper_status)}
                    />
                  )}
                  {rawLink.status_reason.target_oper_status !== undefined && (
                    <DetailRow
                      label="Z end ifOperStatus"
                      value={formatOperStatus(rawLink.status_reason.target_oper_status)}
                    />
                  )}
                  {Number.isFinite(rawLink.status_reason.source_out_bps) && (
                    <DetailRow label="A end out" value={formatMbps(rawLink.status_reason.source_out_bps)} />
                  )}
                  {Number.isFinite(rawLink.status_reason.source_in_bps) && (
                    <DetailRow label="A end in" value={formatMbps(rawLink.status_reason.source_in_bps)} />
                  )}
                  {Number.isFinite(rawLink.status_reason.target_out_bps) && (
                    <DetailRow label="Z end out" value={formatMbps(rawLink.status_reason.target_out_bps)} />
                  )}
                  {Number.isFinite(rawLink.status_reason.target_in_bps) && (
                    <DetailRow label="Z end in" value={formatMbps(rawLink.status_reason.target_in_bps)} />
                  )}
                  {Number.isFinite(rawLink.status_reason.asymmetry_ratio) && (
                    <DetailRow
                      label="Asymmetry ratio"
                      value={rawLink.status_reason.asymmetry_ratio?.toFixed(2)}
                    />
                  )}
                  {Number.isFinite(rawLink.status_reason.ratio_threshold) && (
                    <DetailRow
                      label="Ratio threshold"
                      value={rawLink.status_reason.ratio_threshold?.toFixed(2)}
                    />
                  )}
                  {Number.isFinite(rawLink.status_reason.min_load_threshold_bps) && (
                    <DetailRow
                      label="Load threshold"
                      value={formatMbps(rawLink.status_reason.min_load_threshold_bps)}
                    />
                  )}
                  {Number.isFinite(rawLink.status_reason.near_zero_threshold_bps) && (
                    <DetailRow
                      label="Near-zero threshold"
                      value={formatMbps(rawLink.status_reason.near_zero_threshold_bps)}
                    />
                  )}
                </DetailSection>
              ) : null}

              {rawLink.member_links?.length ? (
                <DetailSection title={`LAG Members (${rawLink.member_links.length})`}>
                  <div className="space-y-2">
                    {rawLink.member_links.map((member, idx) => {
                      const source = formatLagMemberEndpoint(
                        member.source_device_name,
                        member.source_interface_name,
                        member.source_device_id,
                        member.source_interface_id,
                      );
                      const target = formatLagMemberEndpoint(
                        member.target_device_name,
                        member.target_interface_name,
                        member.target_device_id,
                        member.target_interface_id,
                      );
                      return (
                        <div
                          key={`${member.source_interface_id ?? source}-${member.target_interface_id ?? target}-${idx}`}
                          className="rounded border border-border/25 bg-muted/10 px-3 py-2"
                        >
                          <div className="flex items-center justify-between gap-2 text-[11px]">
                            <span className="truncate font-medium text-foreground">{source}</span>
                            <span className="shrink-0 text-muted-foreground/50">→</span>
                            <span className="truncate text-right font-medium text-foreground">{target}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </DetailSection>
              ) : null}

              <DetailSection title="Telemetry Status">
                <DetailRow label="State" value={telemetryStateLabel(viewModel.state)} />
                {viewModel.diagnostic && <DetailRow label="Details" value={viewModel.diagnostic} />}
                {viewModel.telemetryUpdatedAt && (
                  <DetailRow label="Last Update" value={formatAge(Date.parse(viewModel.telemetryUpdatedAt))} />
                )}
                {rawLink.topology_status && (
                  <DetailRow
                    label="Discovery"
                    value={rawLink.topology_status === 'likely' ? 'likely (unconfirmed)' : rawLink.topology_status}
                  />
                )}
                {rawLink.topology_reason && <DetailRow label="Discovery reason" value={rawLink.topology_reason} />}
                {Number.isFinite(rawLink.topology_confidence) && (
                  <DetailRow label="Confidence" value={rawLink.topology_confidence} />
                )}
                {rawLink.topology_evidence_types?.length ? (
                  <DetailRow label="Evidence" value={rawLink.topology_evidence_types.join(', ')} />
                ) : null}
                {rawLink.missing_telemetry?.source?.length > 0 && (
                  <DetailRow
                    label="Missing A-End"
                    value={
                      <ul className="list-inside list-disc">
                        {rawLink.missing_telemetry.source.map(item => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    }
                  />
                )}
                {rawLink.missing_telemetry?.target?.length > 0 && (
                  <DetailRow
                    label="Missing Z-End"
                    value={
                      <ul className="list-inside list-disc">
                        {rawLink.missing_telemetry.target.map(item => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    }
                  />
                )}
              </DetailSection>

              <OpticalLaneSummary
                title="Optical Power (Rx/Tx)"
                metrics={opticsBucket?.endpoints.a.metrics ?? []}
                endpoint="A"
              />
              <OpticalLaneSummary
                title="Optical Power (Rx/Tx)"
                metrics={opticsBucket?.endpoints.z.metrics ?? []}
                endpoint="Z"
              />

              <DetailSection title="Forwarding Chip Temperature">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <div
                      className="mb-2 text-center text-[9px] font-bold uppercase tracking-[0.2em]"
                      style={{ color: 'var(--color-info)' }}
                    >
                      A End
                    </div>
                    <ThermalSummary bucket={tempBucket} endpoint="a" />
                  </div>
                  <div>
                    <div
                      className="mb-2 text-center text-[9px] font-bold uppercase tracking-[0.2em]"
                      style={{ color: 'var(--color-warning)' }}
                    >
                      Z End
                    </div>
                    <ThermalSummary bucket={tempBucket} endpoint="z" />
                  </div>
                </div>
              </DetailSection>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
            <div className="mb-2 h-2 w-2 rounded-full bg-muted/40" />
            <div className="text-xs text-muted-foreground/60">Select a link on the map to view telemetry.</div>
          </div>
        )}
      </div>
    </div>
  );

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Mobile */}
      <div
        ref={mobileDialogRef}
        className="pointer-events-auto absolute inset-0 z-30 md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Link details"
        tabIndex={-1}
        onKeyDown={containMobileFocus}
      >
        <button
          type="button"
          aria-label="Close link details"
          className="absolute inset-0 bg-background/65 backdrop-blur-[2px]"
          onClick={onClose}
        />
        <div className="animate-in slide-in-from-right absolute right-0 top-0 h-full w-full shadow-2xl duration-300">
          {drawerContent}
        </div>
      </div>
      {/* Desktop */}
      <div
        ref={desktopDialogRef}
        className="pointer-events-auto relative z-30 hidden h-full w-[min(50%,760px)] shrink-0 shadow-2xl outline-none md:block"
        role="dialog"
        aria-modal="false"
        aria-label="Link details"
        tabIndex={-1}
      >
        {drawerContent}
      </div>
    </>
  );
};
